import { ArrowRight, Clock, Coins, User } from 'lucide-react'
import { Link } from 'react-router-dom'

import { CourseArtwork } from '@/components/public/course-artwork'
import { saveCoursesOverviewScrollTop } from '@/lib/scroll-container'
import { cn } from '@/lib/utils'
import { formatCourseCredits } from '@/lib/subscriptions'
import type { Course } from '@/types/domain'

type CourseSummaryCardProps = {
  course: Pick<
    Course,
    'name' | 'description' | 'color' | 'duration_minutes' | 'credits_per_session' | 'image_url'
  >
  trainerName: string
  to: string
}

export function CourseSummaryCard({ course, trainerName, to }: CourseSummaryCardProps) {
  return (
    <Link
      to={to}
      state={{ fromCoursesOverview: true }}
      className="block h-full"
      onClick={saveCoursesOverviewScrollTop}
    >
      <article className="group flex h-full flex-col overflow-hidden rounded-xl border border-border bg-card transition-all hover:border-primary/40">
        <div className="relative h-48 overflow-hidden">
          <CourseArtwork
            course={course}
            className="h-full w-full"
            imageClassName="transition-transform duration-500 group-hover:scale-105"
            labelClassName="text-xs text-white/70"
          />
          <div
            className={cn(
              'absolute inset-0 bg-gradient-to-t from-card/52 via-card/8 to-transparent dark:from-card dark:via-card/30',
              !course.image_url && 'from-black/36 via-black/4 to-transparent dark:from-black/60 dark:via-black/10',
            )}
          />
        </div>

        <div className="flex flex-1 flex-col p-5">
          <h2 className="font-headline text-lg font-bold text-foreground transition-colors group-hover:text-primary">
            {course.name}
          </h2>
          <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">
            {course.description?.trim() || 'Weitere Details zu diesem Kurs findest du in der Kursansicht.'}
          </p>

          <div className="mb-4 mt-4 flex items-center gap-4 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <User size={12} />
              {trainerName}
            </span>
            <span className="flex items-center gap-1">
              <Clock size={12} />
              {course.duration_minutes} Min
            </span>
            <span className="flex items-center gap-1">
              <Coins size={12} />
              {formatCourseCredits(course.credits_per_session)}
            </span>
          </div>

          <div className="mt-auto">
            <span className="inline-flex items-center p-0 font-nav text-sm text-primary transition-colors group-hover:text-primary">
              Details ansehen
              <ArrowRight size={14} className="ml-1 transition-transform duration-200 group-hover:translate-x-0.5" />
            </span>
          </div>
        </div>
      </article>
    </Link>
  )
}
